/**
 * Shape rules for an enriched attribute's value: which content types can be
 * edited in place, and how an edited value is converted to the JSON the API
 * expects. Kept out of `attribute-editor.tsx` so that file only exports a
 * component (fast refresh requires it).
 */

import type { OptimizationContentType } from '~/types/enrichment';

/**
 * Content types this editor can round-trip safely. `qa` and `product-details`
 * are excluded on purpose: `qa` is a concatenation across a JSON array that
 * cannot be rebuilt from the rendered pairs without guessing, and
 * `product-details` has its own structured diff surface. Editing either as raw
 * text would risk silently corrupting the value.
 */
const EDITABLE: OptimizationContentType[] = [
  'text',
  'plain',
  'tags',
  'bullets',
  'attributes',
  'structured-json',
];

/**
 * Fields the API refuses regardless of how they render, so the editor must not
 * offer them. Content type alone can't gate these: they aren't in
 * `FIELD_CONTENT_TYPE`, so they fall through to `text` and would otherwise get a
 * textarea whose save is guaranteed to 400.
 *
 * - `retailer_id` (Meta) is the merchant's identifier and the join key delivery
 *   uses; editing it would point the row at a different offer.
 * - `custom_labels` is a fan-out pseudo-field with no column of its own — the
 *   five `custom_label_0..4` slots are the editable surface.
 *
 * Mirrors the service's own reject list; keep the two in step.
 */
const NON_EDITABLE_FIELDS = new Set(['retailer_id', 'custom_labels']);

/** Whether this attribute can be edited in place — by content type, and by name
 *  for fields the API rejects that no content type would catch. */
export function isAttributeEditable(contentType: OptimizationContentType, field?: string): boolean {
  if (field !== undefined && NON_EDITABLE_FIELDS.has(field)) return false;
  return EDITABLE.includes(contentType);
}

/** Splits a delimited value, trimming and dropping blanks — mirrors how
 *  `EnrichmentOptContent` reads the same string back. */
export function splitList(value: string, separator: string): string[] {
  return value
    .split(separator)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** `attributes` entries are `Key: Value` joined by `|`, split on the LAST colon
 *  so multi-colon prefixes survive — same rule the renderer uses. */
export function parsePairs(value: string): { key: string; value: string }[] {
  return splitList(value, '|').map((raw) => {
    const idx = raw.lastIndexOf(':');
    if (idx === -1) return { key: raw, value: '' };
    return { key: raw.slice(0, idx).trim(), value: raw.slice(idx + 1).trim() };
  });
}

/**
 * Converts an edited attribute from its display/wire string into the JSON type
 * the API expects — an object for keyed attributes, an array for list-valued
 * ones, a plain string only for prose. Nothing is sent as stringified JSON: the
 * structure travels as structure so the backend never has to re-parse a string.
 */
export function toAttributePayloadValue(
  contentType: OptimizationContentType,
  value: string,
): unknown {
  if (contentType === 'structured-json') {
    try {
      const parsed = JSON.parse(value) as unknown;
      // Only objects round-trip as objects; anything else goes as-is.
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      // Unparseable drafts fall through to the raw string below.
    }
    return value;
  }

  if (contentType === 'attributes') {
    const pairs = parsePairs(value).filter((pair) => pair.key !== '');
    if (pairs.length === 0) return value;
    return Object.fromEntries(pairs.map((pair) => [pair.key, pair.value]));
  }

  if (contentType === 'tags') return splitList(value, ',');
  if (contentType === 'bullets') return splitList(value, '|');

  // `text` is a list only when it actually carries multiple pipe-delimited items.
  if (contentType === 'text') {
    const items = splitList(value, '|');
    return items.length > 1 ? items : value;
  }

  return value;
}
